/**
 * The desktop's clean-root mode (VolumetricObject.CutawayClean.cs, CleanRootPalette.cs,
 * VolumetricObject.RootClipPlanes.cs): the pipeline's clean render volume (blood hidden inside
 * the root) rendered with the run's per-scan transfer function inside the root REGION mask, the
 * Cardiac VR look outside, and the volume clipped to the root by two planes parallel to the STJ
 * and VBR rings. The manifest side: `clean_ct` + the `region` field + the `tf` asset of a frame
 * (ingest/pipeline.py _emit_extended_volumes).
 */
import * as THREE from 'three';
import type { AssetEntry } from '@/api/client';
import type { TfAlphaPoint, TfColourPoint, TransferFunctionJson } from './tf';

/** Gradient-lighting threshold for samples rendered from the clean volume (CleanRootLightingThreshold). */
export const CLEAN_ROOT_LIGHTING_THRESHOLD: [number, number] = [0.03, 0.1];

/** RootCropBoundsBuilder.CleanRootClipPaddingMm: the STJ / VBR planes sit this far beyond their ring. */
export const CLEAN_ROOT_CLIP_PADDING_MM = 5;

// ---------------------------------------------------------------- palette (CleanRootPalette)
/** Wall and leaflet tissue, held flat from -150 HU to the calcium onset (sRGB). */
export const CLEAN_ROOT_TISSUE: [number, number, number] = [0.89, 0.7, 0.58];
/** Calcium / devices (sRGB). */
export const CLEAN_ROOT_CALCIUM: [number, number, number] = [0.96, 0.94, 0.9];

const TISSUE_HOLD_HU = 386;
const FALLBACK_CALCIUM_START_HU = 700;
const CALCIUM_RAMP_HU = 50;
const MIN_CALCIUM_START_HU = 400;

/** The .tf axis: dataValue = (HU + 1024) / 4095 (identity TF domain). */
export function cleanHuToNormalized(hu: number): number {
  return Math.min(1, Math.max(0, (hu + 1024) / 4095));
}

const STOPS_BELOW_HOLD: [number, [number, number, number]][] = [
  [-1024, [0, 0, 0]],
  [-300, [0, 0, 0]],
  [-150, CLEAN_ROOT_TISSUE],
];

/** CleanRootPalette.FindCalciumRamp: the last transparent alpha stop above the tissue band
 * starts the calcium ramp, the first opaque stop after it ends it; pipeline defaults otherwise. */
export function findCalciumRamp(alphaPoints: TfAlphaPoint[]): { start: number; full: number } {
  let start = cleanHuToNormalized(FALLBACK_CALCIUM_START_HU);
  let full = cleanHuToNormalized(FALLBACK_CALCIUM_START_HU + CALCIUM_RAMP_HU);
  if (!alphaPoints.length) return { start, full };
  const sorted = [...alphaPoints].sort((a, b) => a.dataValue - b.dataValue);
  let lastTransparent = -1;
  for (let i = 0; i < sorted.length; i++) if (sorted[i].alphaValue <= 0.001) lastTransparent = i;
  if (lastTransparent < 0 || sorted[lastTransparent].dataValue < cleanHuToNormalized(MIN_CALCIUM_START_HU)) return { start, full };
  for (let i = lastTransparent + 1; i < sorted.length; i++) {
    if (sorted[i].alphaValue >= 0.5) {
      start = sorted[lastTransparent].dataValue;
      full = Math.max(sorted[i].dataValue, start + 1e-4);
      return { start, full };
    }
  }
  return { start, full };
}

/**
 * CleanRootPalette.Apply: repaint a loaded clean transfer function's colours with the inside-root
 * palette, keeping its per-scan alpha layout, so files written by every pipeline version render
 * the same look (and the leaflet overlay can match it). Returns a new TF.
 */
export function applyCleanRootPalette(tf: TransferFunctionJson): TransferFunctionJson {
  const { start: calciumStart, full: calciumFull } = findCalciumRamp(tf.alphaPoints ?? []);
  const hold = cleanHuToNormalized(TISSUE_HOLD_HU);
  const rgb = (c: [number, number, number]) => ({ r: c[0], g: c[1], b: c[2], a: 1 });
  const stops: TfColourPoint[] = [];
  for (const [hu, colour] of STOPS_BELOW_HOLD) {
    const value = cleanHuToNormalized(hu);
    if (value < hold && value < calciumStart) stops.push({ dataValue: value, colourValue: rgb(colour) });
  }
  if (hold < calciumStart) stops.push({ dataValue: hold, colourValue: rgb(CLEAN_ROOT_TISSUE) });
  stops.push({ dataValue: calciumStart, colourValue: rgb(CLEAN_ROOT_TISSUE) });
  stops.push({ dataValue: calciumFull, colourValue: rgb(CLEAN_ROOT_CALCIUM) });
  stops.push({ dataValue: 1, colourValue: rgb(CLEAN_ROOT_CALCIUM) });
  // Strictly increasing data values (the TF interpolates sorted points).
  let last = -1;
  for (const p of stops) {
    if (p.dataValue <= last) p.dataValue = Math.min(last + 1e-4, 1);
    last = p.dataValue;
  }
  return { version: tf.version ?? 1, colourPoints: stops, alphaPoints: (tf.alphaPoints ?? []).map((p) => ({ ...p })) };
}

// ---------------------------------------------------------------- manifest
export interface CleanRootAssets {
  volume: AssetEntry;
  /** R8 root region mask on the clean volume's grid, or null (the run shipped none: no split look). */
  region: AssetEntry | null;
  /** The run's per-scan .tf JSON, or null (the bundled clean cutaway TF is used). */
  tf: AssetEntry | null;
}

/** The frame's clean volume, region mask and per-scan TF, or null without a clean volume. */
export function cleanRootAssets(assets: AssetEntry[], frame: number): CleanRootAssets | null {
  const of = (a: AssetEntry) => (a.frame_index ?? 0) === frame;
  const volume = assets.find((a) => a.kind === 'clean_ct' && of(a));
  if (!volume || !volume.dims) return null;
  const region = assets.find((a) => a.kind === 'field' && a.meta?.field === 'region' && of(a)) ?? null;
  const tf = assets.find((a) => a.kind === 'tf' && of(a)) ?? null;
  return { volume, region, tf };
}

/** The record's measured window (VolumetricAssetModel.CutawayCleanInfo), normalised; null without one. */
export function cutawayCleanWindow(meta: Record<string, unknown> | undefined): [number, number] | null {
  const rec = meta?.cutaway_clean as Record<string, unknown> | undefined;
  const win = rec?.window as Record<string, unknown> | undefined;
  const lo = typeof win?.min_normalized === 'number' ? win.min_normalized : typeof rec?.window_min_normalized === 'number' ? rec.window_min_normalized : 0;
  const hi = typeof win?.max_normalized === 'number' ? win.max_normalized : typeof rec?.window_max_normalized === 'number' ? rec.window_max_normalized : NaN;
  if (!Number.isFinite(hi) || hi <= lo) return null;
  return [Math.min(1, Math.max(0, lo)), Math.min(1, Math.max(0, hi))];
}

// ---------------------------------------------------------------- root clip planes
/** Best-fit plane normal of a closed ring (Newell's method about the centroid), unnormalised; zero when degenerate. */
export function ringPlaneNormal(points: THREE.Vector3[], centroid: THREE.Vector3): THREE.Vector3 {
  const n = new THREE.Vector3();
  if (points.length < 3) return n;
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  for (let i = 0; i < points.length; i++) {
    a.subVectors(points[i], centroid);
    b.subVectors(points[(i + 1) % points.length], centroid);
    n.add(a.clone().cross(b));
  }
  return n;
}

export function centroidOf(points: THREE.Vector3[]): THREE.Vector3 {
  const c = new THREE.Vector3();
  for (const p of points) c.add(p);
  return c.multiplyScalar(1 / Math.max(1, points.length));
}

function orientedNormal(normal: THREE.Vector3, awayFromRoot: THREE.Vector3): THREE.Vector3 {
  if (normal.lengthSq() < 1e-10) return awayFromRoot.clone();
  const n = normal.clone().normalize();
  return n.dot(awayFromRoot) < 0 ? n.negate() : n;
}

export interface RootClipPlane {
  /** A point on the plane (scene mm). */
  position: THREE.Vector3;
  /** Unit normal pointing away from the root: that half-space is cut. */
  cutSide: THREE.Vector3;
}

/**
 * VolumetricObject.SetRootClipPlanes: one plane parallel to each ring, `paddingMm` beyond it along
 * the ring's own normal, away from the root (the VBR-to-STJ direction decides which way is away).
 * Null when a ring has fewer than three points or the two centres coincide.
 */
export function cleanRootClipPlanes(vbrRing: THREE.Vector3[], stjRing: THREE.Vector3[], paddingMm = CLEAN_ROOT_CLIP_PADDING_MM): { stj: RootClipPlane; vbr: RootClipPlane } | null {
  if (vbrRing.length < 3 || stjRing.length < 3) return null;
  const vbrCentre = centroidOf(vbrRing);
  const stjCentre = centroidOf(stjRing);
  const axis = stjCentre.clone().sub(vbrCentre);
  if (axis.lengthSq() < 1e-10) return null;
  const axisUp = axis.normalize();
  const stjOut = orientedNormal(ringPlaneNormal(stjRing, stjCentre), axisUp);
  const vbrOut = orientedNormal(ringPlaneNormal(vbrRing, vbrCentre), axisUp.clone().negate());
  return {
    stj: { position: stjCentre.clone().addScaledVector(stjOut, paddingMm), cutSide: stjOut },
    vbr: { position: vbrCentre.clone().addScaledVector(vbrOut, paddingMm), cutSide: vbrOut },
  };
}
